import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SystemEntity } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import {
  entityWorkflowContextQueryKey,
  useEntityWorkflowContext,
} from '@/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { isActiveWorkflowRunStatus } from '@/lib/workflows/runStatus';
import { useToast } from '@/context/ToastContext';

/**
 * The reprocess RPC returns once the event is published; the new run is
 * created a moment later by the system-event worker. Re-read the run context
 * immediately and again after this delay so the running state appears even
 * when the first read raced ahead of the worker. Once the new run is loaded,
 * `useEntityWorkflowContext` polls on its own until it settles.
 */
const RUN_APPEARANCE_RECHECK_MS = 2_000;

/**
 * How long after a successful request the button keeps reporting "in
 * progress" on its own, bridging the gap until the new run shows up as
 * active. Long enough to cover the recheck above plus one poll.
 */
const AWAITING_RUN_MS = RUN_APPEARANCE_RECHECK_MS + 4_000;

/**
 * Re-publish `AP_INVOICE_DRAFT_CREATED` for an invoice so the org's
 * processing workflows (line-item matching, Famous fetches, payment
 * scheduling) run again from its current state.
 *
 * `isReprocessing` stays true from the click until the resulting run settles:
 * request in flight → waiting for the worker to create the run → run active.
 */
export function useReprocessAPInvoice(invoiceId: string) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isAwaitingRun, setIsAwaitingRun] = useState(false);
  const awaitingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (awaitingTimerRef.current) clearTimeout(awaitingTimerRef.current);
    },
    [],
  );

  const runContext = useEntityWorkflowContext(
    SystemEntity.AP_INVOICE,
    invoiceId,
  );
  const isRunActive = isActiveWorkflowRunStatus(
    runContext.data?.context?.workflowRun.status,
  );

  function refreshRunSurfaces() {
    void queryClient.invalidateQueries({
      queryKey: entityWorkflowContextQueryKey(
        SystemEntity.AP_INVOICE,
        invoiceId,
      ),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.accountsPayable.invoices.listChangeLog.queryKey({
        input: { apInvoiceId: invoiceId },
      }),
    });
  }

  const mutation = useMutation(
    orpc.accountsPayable.invoices.reprocess.mutationOptions({
      onSuccess: () => {
        showToast({
          title: 'Reprocessing Started',
          message: 'The invoice processing workflow has been queued',
          color: 'success',
          icon: 'check',
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
        });
        refreshRunSurfaces();
        setTimeout(refreshRunSurfaces, RUN_APPEARANCE_RECHECK_MS);

        setIsAwaitingRun(true);
        if (awaitingTimerRef.current) clearTimeout(awaitingTimerRef.current);
        awaitingTimerRef.current = setTimeout(
          () => setIsAwaitingRun(false),
          AWAITING_RUN_MS,
        );
      },
      onError: (error) => {
        showToast({
          title: 'Reprocess Failed',
          message: error.message || 'Failed to reprocess invoice',
          color: 'error',
          icon: 'x',
        });
      },
    }),
  );

  return {
    reprocess: () => mutation.mutate({ id: invoiceId }),
    isReprocessing: mutation.isPending || isAwaitingRun || isRunActive,
  };
}
